import { useEffect, useState } from "react";
import { collection, getDocs, doc, setDoc, deleteDoc, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import OpportunityCard from "../components/OpportunityCard";
import LoadingSpinner from "../components/LoadingSpinner";
import EmptyState from "../components/EmptyState";

const Internships = () => {
  const { user } = useAuth();
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({ paid: "all", remote: "all", duration: "all" });
  const [sortOption, setSortOption] = useState("none");
  const [currentPage, setCurrentPage] = useState(1);
  const [savedInternships, setSavedInternships] = useState({});
  const perPage = 12;

  useEffect(() => {
    const fetchInternships = async () => {
      try {
        const snap = await getDocs(collection(db, "internships"));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setInternships(data);
      } catch (error) {
        console.error("Error fetching internships:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInternships();
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchSaved = async () => {
      const q = query(collection(db, "bookmarks"), where("userId", "==", user.uid));
      const snap = await getDocs(q);
      const saved = {};
      snap.forEach((d) => { saved[d.data().internshipId] = true; });
      setSavedInternships(saved);
    };
    fetchSaved();
  }, [user]);

  const toggleBookmark = async (id) => {
    if (!user) {
      alert("Please sign in to save internships.");
      return;
    }
    const ref = doc(db, "bookmarks", `${user.uid}_${id}`);
    if (savedInternships[id]) {
      await deleteDoc(ref);
      setSavedInternships((prev) => ({ ...prev, [id]: false }));
    } else {
      await setDoc(ref, { userId: user.uid, internshipId: id });
      setSavedInternships((prev) => ({ ...prev, [id]: true }));
    }
  };

  let filtered = internships.filter((job) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      job.title?.toLowerCase().includes(q) ||
      job.company?.toLowerCase().includes(q) ||
      job.location?.toLowerCase().includes(q);

    const matchesPaid =
      filters.paid === "all" ||
      (filters.paid === "paid" && job.paid === true) ||
      (filters.paid === "unpaid" && job.paid === false);

    const matchesRemote =
      filters.remote === "all" ||
      (filters.remote === "remote" && job.remote === true) ||
      (filters.remote === "onsite" && job.remote === false);

    const matchesDuration =
      filters.duration === "all" ||
      (filters.duration === "<3" && job.duration < 3) ||
      (filters.duration === "3-6" && job.duration >= 3 && job.duration <= 6) ||
      (filters.duration === ">6" && job.duration > 6);

    return matchesSearch && matchesPaid && matchesRemote && matchesDuration;
  });

  let sorted = [...filtered];
  if (sortOption === "stipend") sorted.sort((a, b) => (b.stipend || 0) - (a.stipend || 0));
  if (sortOption === "duration") sorted.sort((a, b) => (b.duration || 0) - (a.duration || 0));

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <LoadingSpinner text="Loading internships..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="page-container">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="section-title">Internships</h1>
            <p className="text-gray-500 mt-1">{sorted.length} opportunities available</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <div className="relative sm:col-span-2 lg:col-span-5">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by title, company, or location..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="input-field pl-11"
            />
          </div>

          <select
            name="paid"
            value={filters.paid}
            onChange={(e) => { setFilters({ ...filters, paid: e.target.value }); setCurrentPage(1); }}
            className="filter-select"
          >
            <option value="all">Payment</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>

          <select
            name="remote"
            value={filters.remote}
            onChange={(e) => { setFilters({ ...filters, remote: e.target.value }); setCurrentPage(1); }}
            className="filter-select"
          >
            <option value="all">Work Mode</option>
            <option value="remote">Remote</option>
            <option value="onsite">On-Site</option>
          </select>

          <select
            name="duration"
            value={filters.duration}
            onChange={(e) => { setFilters({ ...filters, duration: e.target.value }); setCurrentPage(1); }}
            className="filter-select"
          >
            <option value="all">Duration</option>
            <option value="<3">Under 3 months</option>
            <option value="3-6">3-6 months</option>
            <option value=">6">6+ months</option>
          </select>

          <select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            className="filter-select"
          >
            <option value="none">Sort by</option>
            <option value="stipend">Stipend (High to Low)</option>
            <option value="duration">Duration (Longest)</option>
          </select>
        </div>

        {paginated.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 animate-fade-in">
              {paginated.map((job) => (
                <OpportunityCard
                  key={job.id}
                  opportunity={job}
                  type="internship"
                  isSaved={!!savedInternships[job.id]}
                  onToggleBookmark={toggleBookmark}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  className="button btn-secondary text-sm"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 px-3">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="button btn-secondary text-sm"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Internships;
